import { createApi } from "@reduxjs/toolkit/query/react";
import { components } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

type ApiResponsePartnerArray =
  components["schemas"]["ApiResponse_PartnerType-Array_"];
type ApiResponseCampaignArray =
  components["schemas"]["ApiResponse_CampaignType-Array_"];
type ApiResponseCampaign = components["schemas"]["ApiResponse_CampaignType_"];
type ApiResponseAdvertiserArray =
  components["schemas"]["ApiResponse_AdvertiserType-Array_"];
type ApiResponseMetricDataArray =
  components["schemas"]["ApiResponse_MetricData-Array_"];
type ApiResponseInsertionOrderArray =
  components["schemas"]["ApiResponse_InsertionOrderType-Array_"];
type ApiResponseCampaignInsertionOrders =
  components["schemas"]["ApiResponse_CampaignInsertionOrdersResponse_"];

type GetCampaignStatsParams = {
  advertiserId: string;
  campaignId: string;
  startDate: string;
  endDate: string;
};

type GetInsertionOrdersParams = {
  advertiserId: string;
  campaignId: string;
};

type GetCampaignByIdParams = {
  advertiserId: string;
  campaignId: string;
};

export const dv360Api = createApi({
  reducerPath: "dv360Api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "DV360Partners",
    "DV360Advertisers",
    "DV360Campaigns",
    "DV360Metrics",
    "DV360InsertionOrders",
  ],
  endpoints: (builder) => ({
    getPartners: builder.query<ApiResponsePartnerArray, void>({
      query: () => "/dv360/getPartners",
      providesTags: ["DV360Partners"],
    }),
    getCampaignsForPartner: builder.query<ApiResponseCampaignArray, string>({
      query: (partnerId) => `/dv360/partners/${partnerId}/campaigns`,
      providesTags: (result, error, partnerId) => [
        { type: "DV360Campaigns", id: `partner-${partnerId}` },
      ],
    }),
    getAdvertisersForPartner: builder.query<ApiResponseAdvertiserArray, string>(
      {
        query: (partnerId) => `/dv360/partners/${partnerId}/advertisers`,
        providesTags: (result, error, partnerId) => [
          { type: "DV360Advertisers", id: `partner-${partnerId}` },
        ],
      }
    ),
    getCampaignsForAdvertiser: builder.query<ApiResponseCampaignArray, string>({
      query: (advertiserId) => `/dv360/advertisers/${advertiserId}/campaigns`,
      providesTags: (result, error, advertiserId) => [
        { type: "DV360Campaigns", id: `advertiser-${advertiserId}` },
      ],
    }),
    getCampaignById: builder.query<ApiResponseCampaign, GetCampaignByIdParams>({
      query: ({ advertiserId, campaignId }) =>
        `/dv360/advertisers/${advertiserId}/campaigns/${campaignId}`,
      providesTags: (result, error, { campaignId }) => [
        { type: "DV360Campaigns", id: campaignId },
      ],
    }),
    getCampaignStats: builder.query<
      ApiResponseMetricDataArray,
      GetCampaignStatsParams
    >({
      query: ({ advertiserId, campaignId, startDate, endDate }) => {
        const params = new URLSearchParams();
        params.append("startDate", startDate);
        params.append("endDate", endDate);
        const queryString = params.toString();
        return `/dv360/advertisers/${advertiserId}/campaigns/${campaignId}/stats?${queryString}`;
      },
      providesTags: (result, error, { campaignId }) => [
        { type: "DV360Metrics", id: campaignId },
      ],
    }),
    getInsertionOrdersForCampaign: builder.query<
      ApiResponseInsertionOrderArray,
      GetInsertionOrdersParams
    >({
      query: ({ advertiserId, campaignId }) =>
        `/dv360/advertisers/${advertiserId}/campaigns/${campaignId}/insertion-orders`,
      providesTags: (result, error, { campaignId }) => [
        { type: "DV360InsertionOrders", id: campaignId },
      ],
    }),
    getCampaignInsertionOrders: builder.query<
      ApiResponseCampaignInsertionOrders,
      string
    >({
      query: (campaignId) => `/dv360/campaigns/${campaignId}/insertion-orders`,
      providesTags: (result, error, campaignId) => [
        { type: "DV360InsertionOrders", id: `campaign-${campaignId}` },
      ],
    }),
  }),
});

export const {
  useGetPartnersQuery,
  useGetCampaignsForPartnerQuery,
  useGetAdvertisersForPartnerQuery,
  useGetCampaignsForAdvertiserQuery,
  useGetCampaignByIdQuery,
  useGetCampaignStatsQuery,
  useGetInsertionOrdersForCampaignQuery,
  useGetCampaignInsertionOrdersQuery,
} = dv360Api;
